/**
 * Consult Door B — the PUBLIC, no-login client booking page.
 *
 * Routes (registered in App.tsx, OUTSIDE RequireAuth):
 *   · /book/type/:slug   — evergreen public link (blank form)
 *   · /book/:token       — record-specific link (pre-filled, editable · R6a)
 *
 * Seven states (spec §4 · datamap §4), matching the QA-passed mockup
 * (Deliverables/2026-07-08-consult-doorB-mockup.html):
 *   1 Landing · 2 Slot picker · 3 Form · 4 You're booked ·
 *   5 Reschedule · 6 Cancelled · 7 Slot just taken.
 *
 * The whole page talks to ONE seam (`BookingApi`) — live App-Check-gated
 * callables by default; a mock provider via `?preview=1` / the harness so QA
 * can walk every state with no backend. When a call cannot run, the page shows
 * the HONEST loading/error state — it never fakes a "booked".
 *
 * AVBP green + system-font tokens only (Iris ruling 2026-07-08 — no serif).
 * Phone-first, one page scroll (no nested scroll box), WCAG 2.2 AA.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DISCOVERY_TYPE,
  liveBookingApi,
  type AvailabilityResult,
  type BookedInfo,
  type BookingApi,
  type CreateBookingResult,
  type PublicAppointmentType,
  type PublicClient,
  type PublicSlot,
} from '../lib/publicBookingApi';
import {
  allTimeZones,
  buildIcs,
  describeSlotView,
  detectClientTz,
  groupByDay,
  shortZoneName,
} from '../lib/publicSlots';
import {
  COUNTRY_DIAL_CODES,
  DIAL_OPTIONS,
  isValidE164,
  splitE164,
  toE164,
} from '../../../lib/countries';
import {
  IconArrowLeft,
  IconArrowRight,
  IconBrowser,
  IconCalendar,
  IconCancel,
  IconChat,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconCopy,
  IconExternal,
  IconGlobe,
  IconInfo,
  IconMail,
  IconMeet,
  IconPencil,
} from './icons';
import '../styles/bookConsult.css';

/* ════════════════════ State machine ════════════════════ */

export type ScreenName =
  | 'landing'
  | 'picker'
  | 'form'
  | 'booked'
  | 'reschedule'
  | 'taken'
  | 'cancelled';

const PRIVACY_URL = 'https://aayatanveda.com/privacy';

/** How many days of availability the picker shows at once (display window). */
const PICKER_WINDOW_DAYS = 7;

/** Hard ceiling on the availability load — the spinner can never outlive this.
 *  Well above the 5s App-Check token bound so a genuinely slow network still
 *  succeeds, but a true stall always resolves to a retryable error state. */
const LOAD_SAFETY_MS = 12_000;

const EMPTY_CLIENT: PublicClient = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  city: '',
  country: '',
  projectType: '',
  discussionTopic: '',
};

export interface BookConsultProps {
  /** Record-specific token (from /book/:token). */
  token?: string;
  /** Public type slug (from /book/type/:slug). Defaults to the launch type. */
  typeSlug?: string;
  /** Harness/preview override — swaps the live seam for the mock provider. */
  apiOverride?: BookingApi;
  /** Harness deep-jump to a state for isolated QA. */
  initialScreen?: ScreenName;
  /** Harness seed — a confirmed booking so booked/reschedule states render. */
  initialBooking?: BookedInfo | null;
  /** Harness seed — fresh slots so the "slot just taken" state renders. */
  initialFreshSlots?: PublicSlot[];
}

/* ════════════════════ Small building blocks ════════════════════ */

function BrandHeader({
  title,
  subtitle,
  large,
}: {
  title: string;
  subtitle?: React.ReactNode;
  large?: boolean;
}) {
  // Each screen is a mutually-exclusive full view, so its title is that view's
  // single <h1> (WCAG 2.4.6 / one-h1-per-view). Only one screen is mounted.
  return (
    <header className="bk-bhead">
      <div className="bk-logo" aria-hidden="true">AV</div>
      <div className="bk-brandname">Aayatan Veda · Vastu &amp; Architecture</div>
      <h1 className={large ? 'bk-htitle bk-htitle-lg' : 'bk-htitle'}>{title}</h1>
      {subtitle && <div className="bk-who">{subtitle}</div>}
    </header>
  );
}

function MetaChips({ type }: { type: PublicAppointmentType }) {
  const priceLabel =
    type.price.mode === 'free'
      ? 'Free'
      : `${type.price.currency ?? ''} ${type.price.amount ?? ''}`.trim();
  return (
    <div className="bk-chips">
      <span className="bk-chip">
        <IconClock size={14} /> {type.durationMin} min
      </span>
      <span className="bk-chip bk-chip-free">{priceLabel}</span>
      <span className="bk-chip">
        <IconMeet size={15} /> Google Meet
      </span>
    </div>
  );
}

/**
 * Timezone bar — AUTO-DETECTS the visitor's own zone and shows times in it by
 * DEFAULT (no forced pick before slots appear). "Change timezone" is an OPTIONAL
 * disclosure that reveals a full IANA zone list (native <select> type-ahead =
 * searchable). Bookable truth stays anchored in IST; this only changes display.
 */
function TzBar({
  tz,
  detected,
  onChange,
}: {
  tz: string;
  detected: string;
  onChange: (tz: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => allTimeZones(), []);
  const selectId = 'bk-tz';
  const isDetected = tz === detected;
  return (
    <div className="bk-tzbar">
      <p className="bk-tz-detected">
        <IconGlobe size={15} />
        <span>
          Times shown in{isDetected ? ' your timezone' : ''} — <b>{shortZoneName(tz)}</b>
        </span>
      </p>
      {!open ? (
        <button
          type="button"
          className="bk-tz-change"
          onClick={() => setOpen(true)}
          aria-expanded={false}
        >
          Change timezone
        </button>
      ) : (
        <div className="bk-tz-picker">
          <label htmlFor={selectId}>Show times in</label>
          <select
            id={selectId}
            value={tz}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Choose the timezone to show times in — type to search"
          >
            {!zones.includes(tz) && <option value={tz}>{tz.replace(/_/g, ' ')}</option>}
            {zones.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </div>
      )}
      <span className="bk-tz-anchor">Bookable times are anchored in India Standard Time.</span>
    </div>
  );
}

function DayRow({
  groups,
  activeDate,
  onPick,
  tz,
}: {
  groups: ReturnType<typeof groupByDay<PublicSlot>>;
  activeDate: string;
  onPick: (dateISO: string) => void;
  tz: string;
}) {
  void tz;
  return (
    <div className="bk-days" role="group" aria-label="Choose a day">
      {groups.map((g) => {
        const active = g.dateISO === activeDate;
        return (
          <button
            key={g.dateISO}
            type="button"
            className="bk-day"
            aria-pressed={active}
            onClick={() => onPick(g.dateISO)}
          >
            <span className="bk-dow">{g.weekday}</span>
            <span className="bk-dn">{g.dayNum}</span>
            <span className="bk-dm">{g.monthShort}</span>
          </button>
        );
      })}
    </div>
  );
}

function SlotButton({
  slot,
  tz,
  onPick,
  showDay,
  busy,
}: {
  slot: PublicSlot;
  tz: string;
  onPick: (slot: PublicSlot) => void;
  showDay?: boolean;
  busy?: boolean;
}) {
  const v = describeSlotView(slot.startISO, slot.endISO, tz);
  return (
    <button
      type="button"
      className="bk-slot"
      onClick={() => onPick(slot)}
      disabled={busy}
      aria-label={`Book ${v.dayFull}, ${v.range}`}
    >
      <span className="bk-slot-main">
        <span className="bk-st">{showDay ? v.dayFull : v.range}</span>
        <span className="bk-sd">{showDay ? v.range : `${describeDuration(slot)} min`}</span>
      </span>
      <span className="bk-slot-arr">
        <IconChevronRight size={18} />
      </span>
    </button>
  );
}

function describeDuration(slot: PublicSlot): number {
  const ms = Date.parse(slot.endISO) - Date.parse(slot.startISO);
  return Number.isFinite(ms) && ms > 0 ? Math.round(ms / 60_000) : 30;
}

/** Honest loading / error panel for the slot area (no dead controls). */
function SlotStatus({
  state,
  onRetry,
}: {
  state: 'loading' | 'error' | 'empty';
  onRetry?: () => void;
}) {
  if (state === 'loading') {
    return (
      <div className="bk-slotstatus" role="status" aria-live="polite">
        <span className="bk-spinner" aria-hidden="true" />
        Loading available times…
      </div>
    );
  }
  if (state === 'empty') {
    return (
      <div className="bk-slotstatus" role="status">
        No open times in this window right now. Please check back a little later.
      </div>
    );
  }
  return (
    <div className="bk-slotstatus bk-slotstatus-err" role="alert">
      <p>We couldn&apos;t load available times just now.</p>
      {onRetry && (
        <button type="button" className="bk-btn bk-btn-line" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Dedicated fallback when the App Check reCAPTCHA token can't settle inside an
 * in-app browser (WhatsApp / Instagram / Line WebViews) — the one case where
 * booking genuinely can't proceed here. Instead of an eternal spinner, we tell
 * the client exactly how to escape the WebView, and give them a real,
 * confirmed-copy link so they can paste it into Safari / Chrome. No dead
 * controls: the Copy button actually copies and confirms.
 */
function WebViewFallback({ onRetry }: { onRetry: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';

  // Light UA sniff — ONLY to tailor "Safari" vs "Chrome" wording. The real
  // trigger is the token timeout, so this fallback works for unnamed WebViews
  // too; the copy below names both when we can't tell.
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const browserName = isIos ? 'Safari' : isAndroid ? 'Chrome' : 'Safari or Chrome';

  const copyLink = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      // Fallback for WebViews without the async Clipboard API.
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="bk-webview" role="status" aria-labelledby="bk-wv-title">
      <span className="bk-webview-ico" aria-hidden="true">
        <IconBrowser size={22} />
      </span>
      <h2 className="bk-webview-title" id="bk-wv-title">
        Please open this page in your browser
      </h2>
      <p className="bk-webview-body">
        To pick your time, tap the <b>•••</b> menu (top-right) and choose{' '}
        <b>Open in Safari</b> (iPhone) or <b>Open in Chrome</b> (Android).
      </p>
      <p className="bk-webview-body bk-webview-orcopy">Or copy the link and open it in {browserName}:</p>
      <div className="bk-webview-urlrow">
        <code className="bk-webview-url">{url}</code>
      </div>
      <button type="button" className="bk-btn bk-btn-primary bk-btn-block" onClick={copyLink}>
        {copied ? (
          <>
            <IconCheck size={16} /> Link copied
          </>
        ) : (
          <>
            <IconCopy size={16} /> Copy link
          </>
        )}
      </button>
      <p className="bk-webview-copied" role="status" aria-live="polite">
        {copied ? 'Link copied — paste it into your browser.' : ''}
      </p>
      <button type="button" className="bk-btn bk-btn-line bk-btn-block" onClick={onRetry}>
        Try again here
      </button>
    </div>
  );
}

/** The tz bar + day selector + slot list, shared by the picker + reschedule
 *  screens. A stable module-level component (never redefined per render) so the
 *  tz <select> keeps focus and the slot list doesn't remount on every keystroke. */
function SlotArea({
  tz,
  detected,
  onTzChange,
  status,
  dayGroups,
  activeDate,
  onSelectDate,
  daySlots,
  onPick,
  onRetry,
}: {
  tz: string;
  detected: string;
  onTzChange: (tz: string) => void;
  status: 'loading' | 'error' | 'webview' | 'ok';
  dayGroups: ReturnType<typeof groupByDay<PublicSlot>>;
  activeDate: string;
  onSelectDate: (dateISO: string) => void;
  daySlots: PublicSlot[];
  onPick: (s: PublicSlot) => void;
  onRetry: () => void;
}) {
  return (
    <>
      {status !== 'webview' && <TzBar tz={tz} detected={detected} onChange={onTzChange} />}
      {status === 'loading' && <SlotStatus state="loading" />}
      {status === 'error' && <SlotStatus state="error" onRetry={onRetry} />}
      {status === 'webview' && <WebViewFallback onRetry={onRetry} />}
      {status === 'ok' && dayGroups.length === 0 && <SlotStatus state="empty" />}
      {status === 'ok' && dayGroups.length > 0 && (
        <>
          <p className="bk-sect">Choose a day</p>
          <DayRow
            groups={dayGroups}
            activeDate={activeDate}
            onPick={onSelectDate}
            tz={tz}
          />
          <p className="bk-sect">Open times</p>
          <div className="bk-slots">
            {daySlots.map((s) => (
              <SlotButton key={s.startISO} slot={s} tz={tz} onPick={onPick} />
            ))}
          </div>
          <p className="bk-picker-note">
            All times shown in your selected timezone. Pick one to continue.
          </p>
        </>
      )}
    </>
  );
}

/* ════════════════════ The page ════════════════════ */

export default function BookConsult({
  token,
  typeSlug,
  apiOverride,
  initialScreen,
  initialBooking = null,
  initialFreshSlots = [],
}: BookConsultProps) {
  const api = useMemo<BookingApi>(
    () => apiOverride ?? liveBookingApi,
    [apiOverride],
  );

  const detected = useMemo(detectClientTz, []);
  const [tz, setTz] = useState(detected);
  const [screen, setScreen] = useState<ScreenName>(initialScreen ?? 'landing');
  const [avail, setAvail] = useState<
    | { status: 'loading' }
    | { status: 'ok'; result: Extract<AvailabilityResult, { kind: 'ok' }> }
    | { status: 'error'; kind: 'generic' | 'webview'; message: string }
  >({ status: 'loading' });

  const [type, setType] = useState<PublicAppointmentType>(DISCOVERY_TYPE);
  const [form, setForm] = useState<PublicClient>(EMPTY_CLIENT);
  // Phone is captured as (country dial code + national number) so we always
  // submit a full E.164 string in `form.phone` — WhatsApp confirmations need
  // the country code (a bare national number can't be delivered). Default +91.
  const [phoneCountry, setPhoneCountry] = useState<string>('India');
  const [phoneNational, setPhoneNational] = useState<string>('');
  const [prefilled, setPrefilled] = useState<Set<keyof PublicClient>>(new Set());
  const [touched, setTouched] = useState<Set<keyof PublicClient>>(new Set());
  const [attempted, setAttempted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  const [pickedSlot, setPickedSlot] = useState<PublicSlot | null>(null);
  const [freshSlots, setFreshSlots] = useState<PublicSlot[]>(initialFreshSlots);
  const [activeDate, setActiveDate] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string>('');
  const [booking, setBooking] = useState<BookedInfo | null>(initialBooking);
  const [icsDone, setIcsDone] = useState(false);
  // Cancel flow (destructive): a two-step in-page confirm + its own error slot
  // so failures are always visible where the user acted — never a silent no-op.
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelError, setCancelError] = useState<string>('');

  const headingRef = useRef<HTMLDivElement | null>(null);
  const liveRef = useRef<HTMLDivElement | null>(null);
  // Focus management for the two-step cancel confirm (a11y).
  const cancelTriggerRef = useRef<HTMLButtonElement | null>(null);
  const cancelKeepRef = useRef<HTMLButtonElement | null>(null);

  const announce = useCallback((msg: string) => {
    if (liveRef.current) liveRef.current.textContent = msg;
  }, []);

  /* ---- load availability ---- */
  const loadAvailability = useCallback(() => {
    let alive = true;
    let settled = false;
    setAvail({ status: 'loading' });
    // Display window: next 7 days (the backend horizon is 60d — this is only how
    // much the picker shows at once).
    const now = new Date();
    const fromISO = now.toISOString();
    const toISO = new Date(now.getTime() + PICKER_WINDOW_DAYS * 86_400_000).toISOString();

    // Overall SAFETY TIMEOUT: even if the seam somehow never resolves (an
    // unforeseen stall below the App-Check bound), the spinner can never live
    // forever — it always resolves to a real, retryable error state.
    const safety = setTimeout(() => {
      if (!alive || settled) return;
      settled = true;
      setAvail({
        status: 'error',
        kind: 'generic',
        message: "We couldn't load available times just now. Please try again in a moment.",
      });
    }, LOAD_SAFETY_MS);

    api
      .getAvailability({
        token,
        typeSlug: typeSlug ?? DISCOVERY_TYPE.slug,
        clientTz: tz,
        fromISO,
        toISO,
        pickerWindowDays: PICKER_WINDOW_DAYS,
      })
      .then((r) => {
        if (!alive || settled) return;
        settled = true;
        clearTimeout(safety);
        if (r.kind === 'ok') {
          setAvail({ status: 'ok', result: r });
          setType(r.type);
          // apply prefill ONCE (only into empty fields — never clobber edits)
          if (r.prefill) {
            const pf = r.prefill;
            const keys = Object.keys(pf) as (keyof PublicClient)[];
            // Phone → split a stored number into (country + national) so both
            // controls show correctly; form.phone keeps the composed E.164.
            const pfPhone = typeof pf.phone === 'string' ? pf.phone.trim() : '';
            let pfPhoneE164 = '';
            if (pfPhone) {
              const parts = splitE164(pfPhone);
              setPhoneCountry(parts.country);
              setPhoneNational(parts.national);
              pfPhoneE164 = isValidE164(pfPhone)
                ? pfPhone
                : toE164(COUNTRY_DIAL_CODES[parts.country] ?? '', parts.national) ?? '';
            }
            setForm((prev) => {
              const next = { ...prev };
              const marks = new Set<keyof PublicClient>();
              for (const k of keys) {
                const val = pf?.[k];
                if (typeof val === 'string' && val && !prev[k]) {
                  next[k] = k === 'phone' ? pfPhoneE164 || val : val;
                  marks.add(k);
                }
              }
              setPrefilled(marks);
              return next;
            });
          }
        } else {
          const isWebView = r.reason === 'appcheck_webview';
          setAvail({
            status: 'error',
            kind: isWebView ? 'webview' : 'generic',
            message: r.message,
          });
          // WCAG 4.1.3 — tell SR users the fallback + escape instructions appeared.
          announce(
            isWebView
              ? 'To pick your time, please open this page in your phone browser — instructions and a copy-link button are shown below.'
              : r.message,
          );
        }
      })
      .catch(() => {
        // The seam degrades honestly (never throws) today, but a defensive
        // .catch guarantees a rejected promise can never strand the spinner.
        if (!alive || settled) return;
        settled = true;
        clearTimeout(safety);
        setAvail({
          status: 'error',
          kind: 'generic',
          message: "We couldn't load available times just now. Please try again in a moment.",
        });
      });
    return () => {
      alive = false;
      clearTimeout(safety);
    };
  }, [api, token, typeSlug, tz, announce]);

  useEffect(() => {
    const cancel = loadAvailability();
    return cancel;
  }, [loadAvailability]);

  /* ---- day groups + selected day ---- */
  const dayGroups = useMemo(
    () =>
      avail.status === 'ok' ? groupByDay(avail.result.slots, tz) : [],
    [avail, tz],
  );
  useEffect(() => {
    if (dayGroups.length && !dayGroups.some((g) => g.dateISO === activeDate)) {
      setActiveDate(dayGroups[0].dateISO);
    }
  }, [dayGroups, activeDate]);

  const daySlots = useMemo(
    () => dayGroups.find((g) => g.dateISO === activeDate)?.slots ?? [],
    [dayGroups, activeDate],
  );

  /* ---- focus management on screen change (a11y) ---- */
  useEffect(() => {
    headingRef.current?.focus();
    // Reset the destructive cancel flow whenever we move screens, so it always
    // opens fresh (no stale confirm/error carried across).
    setCancelConfirm(false);
    setCancelError('');
  }, [screen]);

  /* ---- focus the safe "No, keep it" choice when the confirm panel opens (a11y) ---- */
  useEffect(() => {
    if (cancelConfirm) cancelKeepRef.current?.focus();
  }, [cancelConfirm]);

  /* ---- actions ---- */
  const goPicker = () => setScreen('picker');

  const pickSlotToForm = (slot: PublicSlot) => {
    setPickedSlot(slot);
    setFormError('');
    setScreen('form');
  };

  const submitBooking = async () => {
    if (submitting) return;
    // Hard gate: never allow a booking with blank/invalid contact details.
    const errs = validateClient(form);
    if (!pickedSlot || !consent || Object.keys(errs).length > 0) {
      setAttempted(true);
      // On a failed Enter-submit, move focus to the first invalid field so the
      // client is taken straight to what needs fixing (a11y).
      const firstBadId = errs.firstName
        ? 'fFirst'
        : errs.email
          ? 'fEmail'
          : errs.phone
            ? 'fPhone'
            : null;
      if (firstBadId) {
        requestAnimationFrame(() => document.getElementById(firstBadId)?.focus());
      }
      return;
    }
    setSubmitting(true);
    setFormError('');
    announce('Confirming your booking…');
    const res: CreateBookingResult = await api.createBooking({
      token,
      typeSlug: typeSlug ?? DISCOVERY_TYPE.slug,
      slotStartISO: pickedSlot.startISO,
      slotEndISO: pickedSlot.endISO,
      clientTz: tz,
      client: form,
      consent: true,
    });
    setSubmitting(false);
    if (res.kind === 'ok') {
      setBooking(res.booking);
      setIcsDone(false);
      announce('You are booked.');
      setScreen('booked');
    } else if (res.kind === 'slot_taken') {
      setFreshSlots(res.freshSlots);
      announce('That time was just taken. Here are the next open times.');
      setScreen('taken');
    } else {
      setFormError(res.message);
      announce(res.message);
    }
  };

  const rescheduleTo = async (slot: PublicSlot) => {
    if (!booking?.rescheduleToken || submitting) return;
    setSubmitting(true);
    setFormError('');
    announce('Moving your call…');
    const res = await api.reschedule(
      booking.rescheduleToken,
      slot.startISO,
      slot.endISO,
      tz,
    );
    setSubmitting(false);
    if (res.kind === 'ok') {
      setBooking(res.booking);
      setIcsDone(false);
      announce('Your call was moved.');
      setScreen('booked');
    } else if (res.kind === 'slot_taken') {
      setFreshSlots(res.freshSlots);
      setScreen('taken');
    } else {
      setFormError(res.message);
      announce(res.message);
    }
  };

  const cancelBooking = async () => {
    if (submitting || !booking) return;
    setCancelError('');
    // Never a silent no-op: if we have no self-service token, tell the client
    // exactly what to do instead of swallowing the click (house rule — a dead
    // control / silent failure is precisely what "cancel isn't working" looks
    // like).
    if (!booking.rescheduleToken) {
      // The inline error carries role="alert" — it announces itself, so we
      // don't also push it through the polite live region (avoids a double read).
      setCancelError(
        "We can't cancel this one from here. Please reply to your WhatsApp confirmation, or email care@aayatanveda.in, and we'll cancel it for you right away.",
      );
      return;
    }
    setSubmitting(true);
    announce('Cancelling your call…');
    const res = await api.cancel(booking.rescheduleToken);
    setSubmitting(false);
    if (res.kind === 'ok') {
      announce('Your call is cancelled.');
      setCancelConfirm(false);
      setScreen('cancelled');
    } else {
      // Keep the confirm panel open so the error is visible and the client can
      // retry the same action (never a silent failure). The inline role="alert"
      // announces it — no extra announce() (avoids a double read).
      setCancelError(res.message);
    }
  };

  const downloadIcs = () => {
    if (!booking) return;
    const ics = buildIcs({
      uid: `${booking.consultationId || 'consult'}@aayatanveda`,
      startISO: booking.startISO,
      endISO: booking.endISO,
      summary: `${type.name} with ${type.host} — Aayatan Veda`,
      description: booking.meetUrl
        ? `Google Meet: ${booking.meetUrl}`
        : 'Aayatan Veda consultation',
      location: booking.meetUrl || 'Google Meet',
    });
    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'aayatan-veda-consultation.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    setIcsDone(true);
    announce('Calendar file downloaded.');
  };

  const resetToStart = () => {
    setBooking(null);
    setPickedSlot(null);
    setConsent(false);
    setScreen('landing');
    loadAvailability();
  };

  const updateField = (k: keyof PublicClient, val: string) => {
    setForm((p) => ({ ...p, [k]: val }));
    if (prefilled.has(k)) {
      setPrefilled((prev) => {
        const n = new Set(prev);
        n.delete(k);
        return n;
      });
    }
  };
  const markTouched = (k: keyof PublicClient) =>
    setTouched((prev) => (prev.has(k) ? prev : new Set(prev).add(k)));

  /* ---- phone (country dial code + national → E.164 in form.phone) ---- */
  const setPhoneParts = (country: string, national: string) => {
    setPhoneCountry(country);
    setPhoneNational(national);
    updateField('phone', toE164(COUNTRY_DIAL_CODES[country] ?? '', national) ?? '');
  };

  /* ---- required-field validation (name + email + phone) ---- */
  const clientErrors = validateClient(form);
  const clientValid = Object.keys(clientErrors).length === 0;
  const canConfirm = clientValid && consent && !submitting;
  /** Show a field's error only once it's been touched or a submit was attempted. */
  const fieldError = (k: keyof ClientErrors): string | undefined =>
    touched.has(k) || attempted ? clientErrors[k] : undefined;

  /* ---- shared slot area (picker + reschedule) ---- */
  const slotAvailState: 'loading' | 'error' | 'webview' | 'ok' =
    avail.status === 'error'
      ? avail.kind === 'webview'
        ? 'webview'
        : 'error'
      : avail.status;
  const renderSlotArea = (onPick: (s: PublicSlot) => void) => (
    <SlotArea
      tz={tz}
      detected={detected}
      onTzChange={setTz}
      status={slotAvailState}
      dayGroups={dayGroups}
      activeDate={activeDate}
      onSelectDate={setActiveDate}
      daySlots={daySlots}
      onPick={onPick}
      onRetry={loadAvailability}
    />
  );

  /* ════════════════════ Render ════════════════════ */
  return (
    <div className="bk-root">
      {api.isPreview && (
        <div className="bk-preview-ribbon" role="note">
          Preview — sample data, nothing is saved
        </div>
      )}

      {/* single polite live region for all status announcements */}
      <div className="bk-sr" role="status" aria-live="polite" ref={liveRef} />

      <main className="bk-page">
        {/* ===== 1 · LANDING ===== */}
        {screen === 'landing' && (
          <section className="bk-screen" aria-label="Booking landing">
            <div
              className="bk-focus"
              tabIndex={-1}
              ref={headingRef}
            >
              <BrandHeader
                title={type.name}
                large
                subtitle={
                  <>
                    with <b>{type.host}</b> · Architect &amp; Vastu Acharya
                  </>
                }
              />
              <div className="bk-bhead-chips">
                <MetaChips type={type} />
              </div>
            </div>
            <div className="bk-body">
              <p className="bk-desc">{type.description}</p>
              <ul className="bk-reassure">
                <li>
                  <IconCheck size={16} /> You&apos;ll see only the times {type.host.split(' ')[0]} is genuinely free.
                </li>
                <li>
                  <IconCheck size={16} /> Pick a time in your own timezone — nothing to install.
                </li>
                <li>
                  <IconCheck size={16} /> A Google Meet link and confirmation come straight to you.
                </li>
              </ul>
            </div>
            <div className="bk-foot">
              <button
                type="button"
                className="bk-btn bk-btn-primary bk-btn-block bk-btn-lg"
                onClick={goPicker}
              >
                Pick a time <IconArrowRight size={16} />
              </button>
            </div>
          </section>
        )}

        {/* ===== 2 · PICKER ===== */}
        {screen === 'picker' && (
          <section className="bk-screen" aria-label="Pick a time">
            <div className="bk-focus" tabIndex={-1} ref={headingRef}>
              <BrandHeader
                title="Pick a time"
                subtitle={
                  <>
                    {type.name} · {type.durationMin} min · with <b>{type.host.split(' ')[0]}</b>
                  </>
                }
              />
            </div>
            <div className="bk-body">
              {renderSlotArea(pickSlotToForm)}
            </div>
            <div className="bk-foot">
              <button
                type="button"
                className="bk-btn bk-btn-line bk-btn-block"
                onClick={() => setScreen('landing')}
              >
                <IconArrowLeft size={15} /> Back
              </button>
            </div>
          </section>
        )}

        {/* ===== 3 · FORM ===== */}
        {screen === 'form' && (
          <section className="bk-screen" aria-label="Your details">
            <div className="bk-focus" tabIndex={-1} ref={headingRef}>
              <BrandHeader
                title="Your details"
                subtitle={
                  pickedSlot
                    ? slotLine(pickedSlot, tz)
                    : `${type.name} · ${type.durationMin} min`
                }
              />
            </div>
            <div className="bk-body">
              {prefilled.size > 0 && (
                <div className="bk-prefill-banner">
                  <IconPencil size={18} />
                  <div>
                    We filled in what we already had from your record —{' '}
                    <b>please check it and edit anything that&apos;s not right.</b>
                  </div>
                </div>
              )}

              <form
                className="bk-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  void submitBooking();
                }}
              >
                <div className="bk-grid2">
                  <Field
                    id="fFirst"
                    label="First name"
                    required
                    value={form.firstName}
                    onChange={(v) => updateField('firstName', v)}
                    onBlur={() => markTouched('firstName')}
                    error={fieldError('firstName')}
                    autoComplete="given-name"
                    prefilled={prefilled.has('firstName')}
                  />
                  <Field
                    id="fLast"
                    label="Last name"
                    value={form.lastName}
                    onChange={(v) => updateField('lastName', v)}
                    autoComplete="family-name"
                    placeholder="Your surname"
                    prefilled={prefilled.has('lastName')}
                  />
                </div>
                <Field
                  id="fEmail"
                  label="Email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(v) => updateField('email', v)}
                  onBlur={() => markTouched('email')}
                  error={fieldError('email')}
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  prefilled={prefilled.has('email')}
                />
                <PhoneField
                  country={phoneCountry}
                  national={phoneNational}
                  onCountryChange={(c) => setPhoneParts(c, phoneNational)}
                  onNationalChange={(v) => setPhoneParts(phoneCountry, v)}
                  onBlur={() => markTouched('phone')}
                  error={fieldError('phone')}
                  prefilled={prefilled.has('phone')}
                />
                <div className="bk-grid2">
                  <Field
                    id="fCity"
                    label="City"
                    value={form.city}
                    onChange={(v) => updateField('city', v)}
                    autoComplete="address-level2"
                    placeholder="e.g. Raipur"
                    prefilled={prefilled.has('city')}
                  />
                  <div className="bk-fld">
                    <label htmlFor="fCountry">Country</label>
                    <select
                      id="fCountry"
                      value={form.country}
                      onChange={(e) => updateField('country', e.target.value)}
                    >
                      <option value="">Select…</option>
                      <option>India</option>
                      <option>United Arab Emirates</option>
                      <option>United Kingdom</option>
                      <option>Singapore</option>
                      <option>United States</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div className="bk-fld">
                  <label htmlFor="fProject">Project type</label>
                  <select
                    id="fProject"
                    value={form.projectType}
                    onChange={(e) => updateField('projectType', e.target.value)}
                  >
                    <option value="">Select…</option>
                    <option>New home</option>
                    <option>Home renovation / remodel</option>
                    <option>Plot / property I&apos;m considering</option>
                    <option>Commercial space</option>
                    <option>Astro-Vastu reading</option>
                    <option>Just exploring</option>
                  </select>
                </div>

                <div className="bk-fld bk-topic">
                  <label htmlFor="fTopic">
                    What would you like to discuss with {type.host.split(' ')[0]}?
                  </label>
                  <span className="bk-topic-h">
                    Tell us in your own words — the questions on your mind, what&apos;s
                    happening with your home, anything you&apos;d like {type.host.split(' ')[0]} to
                    look at. This helps make the call genuinely useful for you.
                  </span>
                  <textarea
                    id="fTopic"
                    value={form.discussionTopic}
                    onChange={(e) => updateField('discussionTopic', e.target.value)}
                    placeholder="e.g. We're about to sign for a plot in the north-east and want to know if it's right for us before we commit…"
                  />
                </div>

                {/* The Privacy-Policy disclosure button sits OUTSIDE the <label>
                    so clicking it opens the policy WITHOUT toggling the consent
                    checkbox (a click on anything inside a label activates its
                    control). The label wraps only the consent sentence. */}
                <div className="bk-consent">
                  <input
                    type="checkbox"
                    id="fConsent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span className="bk-consent-text">
                    <label htmlFor="fConsent">
                      I agree to be contacted about this consultation, and I&apos;ve
                      read the{' '}
                    </label>
                    <button
                      type="button"
                      className="bk-link"
                      aria-expanded={policyOpen}
                      aria-controls="bk-policy"
                      onClick={() => setPolicyOpen((o) => !o)}
                    >
                      Privacy Policy
                    </button>
                    .
                  </span>
                </div>
                {policyOpen && (
                  <div className="bk-policy" id="bk-policy">
                    <h3>Privacy Policy</h3>
                    <p>
                      We use your details only to arrange and hold this consultation
                      and to follow up about it. We don&apos;t sell your data. You can
                      ask us to remove it at any time.
                    </p>
                    <a
                      className="bk-link"
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Read the full Privacy Policy <IconExternal size={13} />
                    </a>
                  </div>
                )}

                {formError && (
                  <p className="bk-form-error" role="alert">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  className="bk-btn bk-btn-primary bk-btn-block bk-btn-lg"
                  disabled={!canConfirm}
                >
                  {submitting ? (
                    <>
                      <span className="bk-spinner" aria-hidden="true" /> Confirming…
                    </>
                  ) : (
                    <>
                      Confirm booking <IconCheck size={16} />
                    </>
                  )}
                </button>
                <p className="bk-form-caption">{confirmCaption(clientValid, consent)}</p>
                <button
                  type="button"
                  className="bk-btn bk-btn-line bk-btn-block"
                  onClick={() => setScreen('picker')}
                >
                  <IconArrowLeft size={15} /> Back to times
                </button>
              </form>
            </div>
          </section>
        )}

        {/* ===== 4 · BOOKED ===== */}
        {screen === 'booked' && booking && (
          <section className="bk-screen" aria-label="You're booked">
            <div className="bk-conf-hero bk-focus" tabIndex={-1} ref={headingRef}>
              <div className="bk-bigcheck" aria-hidden="true">
                <IconCheck size={38} />
              </div>
              <h1 className="bk-htitle bk-htitle-lg">You&apos;re booked</h1>
              <div className="bk-who">
                We&apos;ve saved your call with <b>{type.host}</b>.
              </div>
            </div>
            <div className="bk-body">
              {booking.status === 'pending' && (
                <div className="bk-pending-note" role="status">
                  Your booking is saved — we&apos;ll confirm the meeting link shortly.
                </div>
              )}
              <BookedPin slot={{ startISO: booking.startISO, endISO: booking.endISO }} tz={tz} />

              {booking.meetUrl && (
                <a
                  className="bk-meet-link"
                  href={booking.meetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="bk-meet-ico" aria-hidden="true">
                    <IconMeet size={22} />
                  </span>
                  <span className="bk-meet-txt">
                    <b>Join with Google Meet</b>
                    <small>{booking.meetUrl.replace(/^https?:\/\//, '')}</small>
                  </span>
                  <span className="bk-meet-ext" aria-hidden="true">
                    <IconExternal size={16} />
                  </span>
                </a>
              )}

              <button
                type="button"
                className="bk-btn bk-btn-line bk-btn-block"
                onClick={downloadIcs}
              >
                <IconCalendar size={16} /> Add to calendar
              </button>
              {icsDone && (
                <p className="bk-ics-note" role="status">
                  Calendar file downloaded — open it to add the call.
                </p>
              )}

              {/* Honest-states rule: only assert a send the server actually made.
                  Email is always-on (default true unless the CF says otherwise);
                  WhatsApp self-skips until the Meta templates are approved, so we
                  never claim "sent" unless the CF confirms it — otherwise the copy
                  stays future tense ("on its way"). */}
              <div className="bk-sent-strip">
                <div className="bk-sent-card">
                  <IconMail size={18} />
                  <div className="bk-sc-t">
                    {booking.sentChannels?.email === false ? 'Email on its way' : 'Email sent'}
                  </div>
                  <div className="bk-sc-s">confirmation + link</div>
                </div>
                <div className="bk-sent-card">
                  <IconChat size={18} />
                  <div className="bk-sc-t">
                    {booking.sentChannels?.whatsapp === true ? 'WhatsApp sent' : 'WhatsApp on its way'}
                  </div>
                  <div className="bk-sc-s">
                    {booking.sentChannels?.whatsapp === true
                      ? '+ reminder before'
                      : 'confirmation + reminder'}
                  </div>
                </div>
              </div>

              <div className="bk-resched-box">
                <div className="bk-rt">Need to change your time?</div>
                <div className="bk-rs">
                  No problem — you can move or cancel this call yourself.
                </div>
                <button
                  type="button"
                  className="bk-btn bk-btn-line bk-btn-block"
                  onClick={() => setScreen('reschedule')}
                >
                  Reschedule or cancel
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ===== 5 · RESCHEDULE ===== */}
        {screen === 'reschedule' && booking && (
          <section className="bk-screen" aria-label="Reschedule">
            <div className="bk-focus" tabIndex={-1} ref={headingRef}>
              <BrandHeader
                title="Reschedule your call"
                subtitle={
                  <>
                    {type.name} · {type.durationMin} min · with <b>{type.host.split(' ')[0]}</b>
                  </>
                }
              />
            </div>
            <div className="bk-body">
              {/* When reached cold via the emailed /book/manage?t= link we hold
                  the signed token but not the original slot times — show a
                  neutral line rather than an "Invalid Date". */}
              <div className="bk-resched-banner">
                <IconClock size={18} />
                <div>
                  <div className="bk-rb-t">Your call right now</div>
                  <div className="bk-rb-s">
                    {Number.isNaN(Date.parse(booking.startISO))
                      ? 'Your existing consultation'
                      : slotLineText(
                          { startISO: booking.startISO, endISO: booking.endISO },
                          tz,
                        )}
                  </div>
                </div>
              </div>

              {/* Cancel — secondary/destructive, placed up here so it's
                  reachable without scrolling past the whole slot list. Two-step
                  in-page confirm (no browser dialog); errors show inline right
                  where the client acted, never a silent no-op. */}
              <div className="bk-manage-cancel">
                {!cancelConfirm ? (
                  <button
                    ref={cancelTriggerRef}
                    type="button"
                    className="bk-btn bk-btn-danger bk-btn-block"
                    onClick={() => {
                      setCancelError('');
                      setCancelConfirm(true);
                    }}
                    disabled={submitting}
                  >
                    <IconCancel size={15} /> Cancel this booking
                  </button>
                ) : (
                  <div className="bk-cancel-confirm" role="group" aria-label="Confirm cancellation">
                    <p className="bk-cc-q">
                      Cancel this call? You&apos;re always welcome to book again later.
                    </p>
                    <div className="bk-cc-actions">
                      <button
                        type="button"
                        className="bk-btn bk-btn-danger"
                        onClick={() => void cancelBooking()}
                        disabled={submitting}
                      >
                        {submitting ? 'Cancelling…' : 'Yes, cancel it'}
                      </button>
                      <button
                        ref={cancelKeepRef}
                        type="button"
                        className="bk-btn bk-btn-line"
                        onClick={() => {
                          setCancelConfirm(false);
                          setCancelError('');
                          // Return focus to the trigger it re-mounts into (a11y).
                          requestAnimationFrame(() => cancelTriggerRef.current?.focus());
                        }}
                        disabled={submitting}
                      >
                        No, keep it
                      </button>
                    </div>
                  </div>
                )}
                {cancelError && (
                  <p className="bk-form-error" role="alert">
                    {cancelError}
                  </p>
                )}
              </div>

              <p className="bk-sect">Or pick a new day &amp; time</p>
              {renderSlotArea(rescheduleTo)}
              <p className="bk-picker-note">
                Pick a new time — we&apos;ll move your call and keep the same Meet link.
              </p>

              {formError && (
                <p className="bk-form-error" role="alert">
                  {formError}
                </p>
              )}
            </div>
            <div className="bk-foot">
              <button
                type="button"
                className="bk-btn bk-btn-line bk-btn-block"
                onClick={() => setScreen('booked')}
              >
                <IconArrowLeft size={15} /> Keep my current time
              </button>
            </div>
          </section>
        )}

        {/* ===== 6 · SLOT JUST TAKEN ===== */}
        {screen === 'taken' && (
          <section className="bk-screen" aria-label="Slot just taken">
            <div className="bk-focus" tabIndex={-1} ref={headingRef}>
              <BrandHeader
                title="Almost — let's pick another"
                subtitle={
                  <>
                    {type.name} · {type.durationMin} min · with <b>{type.host.split(' ')[0]}</b>
                  </>
                }
              />
            </div>
            <div className="bk-body">
              <div className="bk-edge-hero">
                <span className="bk-edge-ico" aria-hidden="true">
                  <IconInfo size={20} />
                </span>
                <div>
                  <div className="bk-eh-t">That time was just taken</div>
                  <div className="bk-eh-s">
                    Someone booked it a moment before you. No worries — here are the
                    next open times.
                  </div>
                </div>
              </div>

              <p className="bk-sect">Next open times</p>
              <div className="bk-slots">
                {freshSlots.map((s) => (
                  <SlotButton
                    key={s.startISO}
                    slot={s}
                    tz={tz}
                    onPick={pickSlotToForm}
                    showDay
                  />
                ))}
                {freshSlots.length === 0 && <SlotStatus state="empty" />}
              </div>
            </div>
            <div className="bk-foot">
              <button
                type="button"
                className="bk-btn bk-btn-line bk-btn-block"
                onClick={() => setScreen('picker')}
              >
                See all available times
              </button>
            </div>
          </section>
        )}

        {/* ===== 7 · CANCELLED ===== */}
        {screen === 'cancelled' && (
          <section className="bk-screen" aria-label="Booking cancelled">
            <div className="bk-cancel-hero bk-focus" tabIndex={-1} ref={headingRef}>
              <div className="bk-cancel-ic" aria-hidden="true">
                <IconCancel size={34} />
              </div>
              <h1 className="bk-htitle">Your call is cancelled</h1>
              <p className="bk-cancel-p">
                We&apos;ve cancelled the consultation and let the office know. You&apos;ll
                get a short confirmation by email and WhatsApp.
              </p>
            </div>
            <div className="bk-body">
              <div className="bk-resched-box">
                <div className="bk-rt">Changed your mind?</div>
                <div className="bk-rs">
                  You&apos;re always welcome to book a new time whenever suits you.
                </div>
                <button
                  type="button"
                  className="bk-btn bk-btn-primary bk-btn-block"
                  onClick={resetToStart}
                >
                  Book a new time
                </button>
              </div>
            </div>
          </section>
        )}
      </main>

      <p className="bk-footer-note">
        Aayatan Veda · Vastu &amp; Architecture — bookable times anchored in India
        Standard Time, shown in your timezone.
      </p>
    </div>
  );
}

/* ════════════════════ Field + slot-line helpers ════════════════════ */

function Field({
  id,
  label,
  hint,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
  autoComplete,
  inputMode,
  prefilled,
  required,
  error,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'tel';
  prefilled?: boolean;
  required?: boolean;
  /** Shown (and marks the field invalid) only when set — caller decides when. */
  error?: string;
}) {
  const invalid = Boolean(error);
  const errId = `${id}-err`;
  return (
    <div className={`bk-fld${prefilled ? ' bk-prefilled' : ''}${invalid ? ' bk-fld-invalid' : ''}`}>
      <label htmlFor={id}>
        {label}
        {required && <span className="bk-req" aria-hidden="true"> *</span>}
        {hint && <span className="bk-opt"> {hint}</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errId : undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      {invalid ? (
        <span className="bk-fld-err" id={errId} role="alert">
          {error}
        </span>
      ) : (
        prefilled && (
          <span className="bk-prefill-hint">
            <IconCheck size={12} /> We filled this in — edit if needed
          </span>
        )
      )}
    </div>
  );
}

/**
 * Phone-with-country-code field. A dial-code <select> (country calling code,
 * default +91 India, full canonical list) + the national number input, composed
 * into a full E.164 in form.phone so WhatsApp confirmations can deliver. Both
 * controls are labelled/aria-wired; the error is shared. No dead controls.
 */
function PhoneField({
  country,
  national,
  onCountryChange,
  onNationalChange,
  onBlur,
  error,
  prefilled,
}: {
  country: string;
  national: string;
  onCountryChange: (country: string) => void;
  onNationalChange: (national: string) => void;
  onBlur?: () => void;
  error?: string;
  prefilled?: boolean;
}) {
  const invalid = Boolean(error);
  const errId = 'fPhone-err';
  return (
    <div className={`bk-fld${prefilled ? ' bk-prefilled' : ''}${invalid ? ' bk-fld-invalid' : ''}`}>
      <label htmlFor="fPhone">
        Phone
        <span className="bk-req" aria-hidden="true"> *</span>
        <span className="bk-opt"> (WhatsApp)</span>
      </label>
      <div className="bk-phone-row">
        <select
          id="fPhoneCc"
          className="bk-phone-cc"
          value={country}
          onChange={(e) => onCountryChange(e.target.value)}
          onBlur={onBlur}
          aria-label="Country calling code"
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errId : undefined}
        >
          {DIAL_OPTIONS.map((o) => (
            <option key={o.name} value={o.name}>
              {o.name} ({o.dial})
            </option>
          ))}
        </select>
        <input
          id="fPhone"
          className="bk-phone-num"
          type="tel"
          value={national}
          placeholder="98765 43210"
          autoComplete="tel-national"
          inputMode="tel"
          required
          aria-required
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errId : undefined}
          onChange={(e) => onNationalChange(e.target.value)}
          onBlur={onBlur}
        />
      </div>
      {invalid ? (
        <span className="bk-fld-err" id={errId} role="alert">
          {error}
        </span>
      ) : prefilled ? (
        <span className="bk-prefill-hint">
          <IconCheck size={12} /> We filled this in — edit if needed
        </span>
      ) : (
        <span className="bk-phone-hint">
          Pick your country code, then your number — so we can WhatsApp your
          confirmation.
        </span>
      )}
    </div>
  );
}

/* ---- pure required-field validation (name + email + phone) ---- */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ClientErrors {
  firstName?: string;
  email?: string;
  phone?: string;
}

/** Validate the three REQUIRED contact fields. A booking must never be possible
 *  with blank/invalid name, email or phone (owner rule; Mack re-enforces on the
 *  backend). Other fields stay optional. */
export function validateClient(c: PublicClient): ClientErrors {
  const e: ClientErrors = {};
  if (!c.firstName.trim()) e.firstName = 'Please enter your first name.';
  if (!c.email.trim()) e.email = 'Please enter your email address.';
  else if (!EMAIL_RE.test(c.email.trim())) e.email = 'Please enter a valid email address.';
  // Phone is composed as a full E.164 (`+<country><national>`) by PhoneField, so
  // a bare national number never slips through — WhatsApp needs the country code.
  if (!c.phone.trim()) e.phone = 'Please enter your phone number with its country code.';
  else if (!isValidE164(c.phone)) e.phone = 'Please enter a valid phone number with country code.';
  return e;
}

/** Dynamic guidance under the Confirm button — tells the client exactly what's
 *  still needed so the disabled state is never a mystery. */
function confirmCaption(clientValid: boolean, consent: boolean): string {
  if (!clientValid && !consent)
    return 'Add your name, email and phone, then tick the box to confirm.';
  if (!clientValid) return 'Please complete your name, email and phone to confirm.';
  if (!consent) return 'Tick the box above to confirm. You can reschedule or cancel any time.';
  return 'All set — tap Confirm booking to lock in your time.';
}

function slotLine(slot: PublicSlot, tz: string): string {
  return slotLineText(slot, tz);
}

function slotLineText(slot: PublicSlot, tz: string): string {
  const v = describeSlotView(slot.startISO, slot.endISO, tz);
  return `${v.dayFull} · ${v.range} (${shortZoneName(tz)})`;
}

function BookedPin({ slot, tz }: { slot: PublicSlot; tz: string }) {
  const v = describeSlotView(slot.startISO, slot.endISO, tz);
  return (
    <div className="bk-conf-pin">
      <div className="bk-cal-ic" aria-hidden="true">
        <div className="bk-cal-top">{v.monthShort.toUpperCase()}</div>
        <div className="bk-cal-num">{v.dayNum}</div>
      </div>
      <div className="bk-cp-d">
        <div className="bk-cp-big">{v.dayFull}</div>
        <div className="bk-cp-sm">
          {v.range} · {shortZoneName(tz)}
        </div>
      </div>
    </div>
  );
}
